"use client"

import { useState, useEffect, useCallback } from "react"
import { FaMagnifyingGlass } from "react-icons/fa6"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../firebase"
import SportsEvent from "./SportsEvent"
import Filters from "./Filters"

const SportSection = ({ onSelectBet, onSelectedSportKeysFromList, onSelectedSportTitleFromList, searchQuery, onSearchQueryChange }) => {
  const [selectedBet, setSelectedBet] = useState(null)
  const [filteredEvents, setFilteredEvents] = useState([])
  const [selectedFilter, setSelectedFilter] = useState("All")
  const [selectedSportKeys, setSelectedSportKeys] = useState(["all"]) // Changed to array
  const [allScores, setAllScores] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAllScores = async () => {
      try {
        setLoading(true)
        const scoresCollection = collection(db, "Scores")
        const scoresSnapshot = await getDocs(scoresCollection)
        const scoresData = scoresSnapshot.docs.map((doc) => doc.data())

        const allScoresArray = scoresData.flatMap((scoreGroup) =>
          scoreGroup.scores.map((score) => ({
            ...score,
            id: score.id || Math.random().toString(36).substr(2, 9),
          })),
        )

        setAllScores(allScoresArray)
      } catch (error) {
        console.error("Error fetching scores:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchAllScores()
  }, [])

  // Updated filtering logic to handle array of sport keys
  useEffect(() => {
    if (!allScores.length) return

    const normalizedSearch = (searchQuery || "").trim().toLowerCase()
    const now = new Date()
    const filtered = allScores.filter((event) => {
      const eventDate = new Date(event.commence_time)
      const isValidDate = !isNaN(eventDate.getTime())
      const isFutureEvent = isValidDate && eventDate > now

      // Check if the event's sport_key matches any in the selected keys array
      const sportKeyMatch =
        selectedSportKeys.includes("all") || selectedSportKeys.some((key) => event.sport_key === key)

      const matchesQuery = !normalizedSearch
        ? true
        : [
            event.home_team,
            event.away_team,
            event.sport_title,
            event.sport_key,
          ].some((value) => (value || "").toString().toLowerCase().includes(normalizedSearch))

      // If searching, ignore the currently selected sport filter and search across everything.
      return isFutureEvent && matchesQuery && (normalizedSearch ? true : sportKeyMatch)
    })

    filtered.sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time))
    setFilteredEvents(filtered)
  }, [selectedSportKeys, allScores, searchQuery])

  useEffect(() => {
    setSelectedFilter(onSelectedSportTitleFromList || "All")
    setSelectedSportKeys(onSelectedSportKeysFromList || ["all"])
  }, [onSelectedSportKeysFromList])

  const handleSelectBet = useCallback(
    (event) => {
      setSelectedBet(event)
      onSelectBet(event)
    },
    [onSelectBet],
  )

  // Updated to handle array of sport keys
  const handleFilterSelect = useCallback((sportsKey, sportsTitle) => {
    setSelectedFilter(sportsTitle)
    setSelectedSportKeys(Array.isArray(sportsKey) ? sportsKey : [sportsKey])
  }, [])

  return (
    <div className="w-full rounded-xl mb-6 min-h-full bg-gray-900/30 backdrop-blur-sm border border-gray-800 overflow-hidden">
      <div className="w-full h-[150px] md:h-[220px] bg-cover bg-center relative">
        <img
          alt=""
          src={require("../assets/mainBackground.png") || "/placeholder.svg"}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900"></div>
      </div>

      <Filters onFilterSelect={handleFilterSelect} />

      <div className="px-4 -mt-1 md:hidden">
        <div className="w-full items-center flex p-2 rounded-full bg-gray-800/70 text-white">
          <span className="px-3 border-none">
            <FaMagnifyingGlass size={12} color="white" />
          </span>
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange?.(e.target.value)}
            className="bg-transparent w-full focus:outline-none"
            aria-label="Search games"
          />
        </div>
      </div>

      <div className="flex justify-between items-center px-5 py-7">
        <h1 className="flex text-white text-lg font-bold gap-4 ml-2">
          {(searchQuery || "").trim()
            ? `Search: ${searchQuery.trim()}`
            : selectedFilter || "All"}
          <div className="flex items-center gap-2 ml-5">
            <div className="w-1 h-4 bg-green-500 rounded-full"></div>
            <span className="text-gray-400 text-sm">Home</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-blue-400 rounded-full"></div>
            <span className="text-gray-400 text-sm">Away</span>
          </div>
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center p-8">
          <div className="bg-gray-800/70 p-4 rounded-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mx-auto"></div>
            <span className="text-white block mt-4">Loading events...</span>
          </div>
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
          {filteredEvents.map((event) => (
            <div key={event.id} className="w-full">
              <SportsEvent event={event} onSelect={handleSelectBet} />
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center">
          <div className="bg-gray-800/70 p-6 rounded-xl inline-block mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 text-gray-500 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl text-white font-medium mb-2">
            {(searchQuery || "").trim() ? "No Search Results" : "No Events Available"}
          </h2>
          <p className="text-gray-400">
            {(searchQuery || "").trim()
              ? "Try a different search term."
              : "Try selecting a different sport or check back later"}
          </p>
        </div>
      )}
    </div>
  )
}

export default SportSection

